import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import ChartFrame from "../../components/ChartFrame";
import { fmtRelative } from "../../lib/utils-app";
import { BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { Activity, Rss, FileEdit, CheckCircle2, AlertTriangle, Sparkles, TrendingUp, ArrowUpRight } from "lucide-react";

const Metric = ({ label, value, icon: Icon, tone = "", hint, testId }) => (
  <div className="io-card-el p-5 relative overflow-hidden" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="kicker" style={{ color: tone === "gold" ? "#D4AF37" : tone === "red" ? "#EF4444" : "#94A3B8" }}>{label}</div>
        <div className={`font-serif text-[36px] mt-2 leading-none ${tone === "gold" ? "text-[var(--io-gold)]" : tone === "red" ? "text-[#EF4444]" : "text-[var(--io-text)]"}`}>{value}</div>
        {hint && <div className="mt-2 text-[11px] font-mono text-[var(--io-muted)]">{hint}</div>}
      </div>
      {Icon && <Icon size={18} className={tone === "gold" ? "text-[var(--io-gold)]" : tone === "red" ? "text-[#EF4444]" : "text-[var(--io-text-2)]"}/>}
    </div>
  </div>
);

const CHART_COLORS = ["#D4AF37", "#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [catDist, setCatDist] = useState([]);

  useEffect(() => {
    document.title = "Command Center — IndiaODR Admin";
    api.get("/admin/dashboard").then((r) => setData(r.data));
    api.get("/categories").then((r) => setCatDist(r.data.filter((c) => c.count > 0)));
  }, []);

  if (!data) return <AdminLayout><div className="text-[var(--io-muted)]">Loading command center…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow" style={{ color: "#D4AF37" }}>Intelligence Overview</div>
          <h1 className="font-serif text-[32px] md:text-[40px] mt-1 text-[var(--io-text)]">IndiaODR Command Center</h1>
          <p className="mt-1 text-[13px] font-mono text-[var(--io-muted)]">
            <span className="signal-dot inline-block mr-2 align-middle"/>Live · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="flex gap-2" data-testid="quick-actions">
          <Link to="/admin/feed" className="btn btn-ghost !py-2 !px-3 !text-[12px]"><Rss size={14}/> Feed</Link>
          <Link to="/admin/drafts" className="btn btn-primary !py-2 !px-3 !text-[12px]"><FileEdit size={14}/> Review Queue</Link>
        </div>
      </div>

      {/* METRICS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Metric label="Active sources" value={data.active_sources} icon={Rss} testId="kpi-active-sources" hint={`of ${data.total_sources} total`}/>
        <Metric label="Fetched today" value={data.fetched_today} icon={TrendingUp} testId="kpi-fetched-today"/>
        <Metric label="Drafts pending" value={data.drafts_pending} icon={FileEdit} tone="gold" testId="kpi-drafts-pending"/>
        <Metric label="Published" value={data.published} icon={CheckCircle2} testId="kpi-published"/>
        <Metric label="High-risk drafts" value={data.high_risk_drafts} icon={AlertTriangle} tone="red" testId="kpi-high-risk"/>
        <Metric label="AI confidence" value="94%" icon={Sparkles} tone="gold" testId="kpi-ai-confidence" hint="rolling avg"/>
      </div>

      {/* CHARTS ROW */}
      <div className="grid lg:grid-cols-3 gap-5 mb-8">
        <div className="io-card-el p-5 lg:col-span-2" data-testid="chart-by-category">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Articles by vertical</div>
              <h3 className="font-serif text-[18px] mt-1 text-[var(--io-text)]">Coverage distribution</h3>
            </div>
            <Activity size={16} className="text-[var(--io-gold)]"/>
          </div>
          <ChartFrame className="h-[220px] -ml-2">
            {(w, h) => (
              <BarChart width={w} height={h} data={catDist} margin={{ top: 10, right: 10, bottom: 50, left: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} angle={-30} textAnchor="end" interval={0} height={50}/>
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} width={24}/>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 6, fontSize: 12, color: "#F8FAFC" }} cursor={{ fill: "rgba(212,175,55,0.05)" }}/>
                <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]}/>
              </BarChart>
            )}
          </ChartFrame>
        </div>

        <div className="io-card-el p-5" data-testid="chart-composition">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Composition</div>
              <h3 className="font-serif text-[18px] mt-1 text-[var(--io-text)]">Top verticals</h3>
            </div>
          </div>
          <ChartFrame className="h-[220px]">
            {(w, h) => (
              <PieChart width={w} height={h}>
                <Pie data={catDist.slice(0, 6)} dataKey="count" nameKey="name" innerRadius={48} outerRadius={82} paddingAngle={2}>
                  {catDist.slice(0, 6).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#0B1020"/>)}
                </Pie>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 6, fontSize: 12, color: "#F8FAFC" }}/>
              </PieChart>
            )}
          </ChartFrame>
        </div>
      </div>

      {/* SECOND CHARTS ROW */}
      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        <div className="io-card-el p-5" data-testid="chart-by-region">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Regional coverage</div>
              <h3 className="font-serif text-[18px] mt-1 text-[var(--io-text)]">Articles by region</h3>
            </div>
          </div>
          <ChartFrame className="h-[240px] -ml-2">
            {(w, h) => (
              <BarChart width={w} height={h} data={data.by_region || []} layout="vertical" margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B"/>
                <XAxis type="number" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <YAxis type="category" dataKey="region" tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" }} width={90}/>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 6, fontSize: 12, color: "#F8FAFC" }} cursor={{ fill: "rgba(37,99,235,0.08)" }}/>
                <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]}/>
              </BarChart>
            )}
          </ChartFrame>
        </div>

        <div className="io-card-el p-5" data-testid="chart-frequency">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Publishing frequency</div>
              <h3 className="font-serif text-[18px] mt-1 text-[var(--io-text)]">Last 8 weeks</h3>
            </div>
            <TrendingUp size={16} className="text-[var(--io-gold)]"/>
          </div>
          <ChartFrame className="h-[240px] -ml-2">
            {(w, h) => (
              <LineChart width={w} height={h} data={data.by_week || []} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B"/>
                <XAxis dataKey="week" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }}/>
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} width={24} allowDecimals={false}/>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 6, fontSize: 12, color: "#F8FAFC" }}/>
                <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37", r: 3 }} activeDot={{ r: 5 }}/>
              </LineChart>
            )}
          </ChartFrame>
        </div>
      </div>

      {/* LOWER GRID */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="io-card-el p-5" data-testid="top-articles-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Top articles</div>
            <Link to="/admin/articles" className="text-[11px] font-mono text-[var(--io-gold)] hover:underline inline-flex items-center gap-1">All <ArrowUpRight size={12}/></Link>
          </div>
          {data.top_articles.length === 0 && <div className="text-[var(--io-muted)] text-sm py-6">No articles yet.</div>}
          <div className="divide-y divide-[var(--io-border)]">
            {data.top_articles.map((a, i) => (
              <div key={a.slug} className="py-3 flex items-start justify-between gap-4" data-testid={`top-article-${a.slug}`}>
                <div className="flex gap-3 min-w-0">
                  <span className="font-mono text-[11px] text-[var(--io-muted)] pt-0.5 w-5 shrink-0">{(i + 1).toString().padStart(2, "0")}</span>
                  <Link to={`/article/${a.slug}`} target="_blank" className="font-sans text-[13px] text-[var(--io-text)] hover:text-[var(--io-gold)] truncate">{a.headline}</Link>
                </div>
                <span className="shrink-0 font-mono text-[11px] text-[var(--io-gold)]">{a.views} views</span>
              </div>
            ))}
          </div>
        </div>

        <div className="io-card-el p-5" data-testid="audit-panel">
          <div className="eyebrow mb-4">Editorial activity log</div>
          <div className="divide-y divide-[var(--io-border)] text-[13px]">
            {data.recent_audit.map((l) => (
              <div key={l.id} className="py-2.5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span className="font-mono text-[11px] text-[var(--io-gold)]">{l.action}</span>
                  <span className="ml-2 text-[var(--io-text-2)] truncate">{l.actor}</span>
                </div>
                <span className="text-[11px] font-mono text-[var(--io-muted)] shrink-0">{fmtRelative(l.at)}</span>
              </div>
            ))}
            {data.recent_audit.length === 0 && <div className="py-6 text-[var(--io-muted)]">No recent activity.</div>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
